import React from 'react';
import { bool, func, shape, string } from 'prop-types';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import { variant } from 'styled-system';
import styled from 'styled-components';
import Styled from '../../utils/Styled';
import Row from '../Container/Row';
import { BodyText, SubBodyText } from '../Text/Heading';
import { Variant } from '../../utils/constants';
import LOBIcon from '../Icon';

const PlaceholderPrefix = Styled(BodyText, {
  color: 'greyDark',
  fontSize: 14,
  opacity: 0.5,
  lineHeight: '24px',
});

const Placeholder = Styled(PlaceholderPrefix, {
  fontWeight: 400,
});

const PlaceholderContainer = Styled(Row, {
  flex: 1,
  marginY: '16px',
});

const TextBox = Styled(Row, {
  marginY: '8px',
  paddingRight: '10px',
  flexDirection: 'column',
  flex: 1,
});

const Container = styled(Row)`
  scroll-margin-top: 56px;
`;

const PrimaryPrefix = Styled(BodyText, {
  color: 'darkBlack',
  fontSize: 14,
});

const PrimaryText = Styled(PrimaryPrefix, {
  fontWeight: 400,
});
PrimaryText.displayName = 'PrimaryText';

const SecondaryText = Styled(
  SubBodyText,
  {
    color: 'greyDark',
    lineHeight: '20px',
  },
  `word-break: break-all;`
);

SecondaryText.displayName = 'SecondaryText';

const Box = Styled(
  Row,
  {
    width: 1,
    border: '1px solid',
    borderColor: 'greyLight',
    borderRadius: 4,
    paddingX: '16px',
    alignItems: 'center',
  },
  variant({
    variants: {
      [Variant.Error]: {
        borderColor: 'tertiaryOrange',
      },
    },
  })
);

const RightContent = Styled(Row, {
  color: 'greyDark',
});

const Icon = Styled(
  LOBIcon,
  {
    name: 'chevron-right',
    size: 24,
  },
  variant({
    variants: {
      [Variant.Error]: {
        color: 'tertiaryOrange',
      },
      [Variant.Default]: {
        color: 'greyDark',
      },
    },
  })
);

const EditText = Styled(BodyText, {
  fontSize: 12,
  lineHeight: '24px',
  color: 'primary',
});

const EditTextMove = Styled(BodyText, {
  fontSize: 12,
  lineHeight: '24px',
  color: 'tertiaryRed',
});

const FillInDetails = ({
  id,
  onClick,
  onEdit,
  placeholderPrefix,
  error,
  content = {},
  move,
}) => {
  const { prefix, primary, secondary } = content;
  return (
    <Container id={id} className="guest">
      <Box variant={error ? Variant.Error : Variant.Default} onClick={onClick}>
        {primary ? (
          <TextBox className="guest-desc">
            <PrimaryText className="primary-text">
              <PrimaryPrefix>
                {prefix && `${prefix}: `}
                {primary}
              </PrimaryPrefix>
            </PrimaryText>
            {secondary && (
              <SecondaryText className="secondary-text">
                {secondary}
              </SecondaryText>
            )}
          </TextBox>
        ) : (
          <PlaceholderContainer>
            {placeholderPrefix && (
              <PlaceholderPrefix>{placeholderPrefix}:&nbsp;</PlaceholderPrefix>
            )}
            <Placeholder>{translate('fillInDetails.placeholder')}</Placeholder>
          </PlaceholderContainer>
        )}
        <RightContent onClick={onEdit}>
          {primary ? (
            move ? (
              <EditTextMove className="edit-button" move={move}>
                {translate('fillInDetails.editBtn')}
              </EditTextMove>
            ) : (
              <EditText className="edit-button" move={move}>
                {translate('fillInDetails.editBtn')}
              </EditText>
            )
          ) : (
            <Icon
              icon="ChevronRightIcon"
              fontsize="titleNormal"
              variant={error ? Variant.Error : Variant.Default}
            />
          )}
        </RightContent>
      </Box>
    </Container>
  );
};

FillInDetails.propTypes = {
  content: shape({
    prefix: string,
    primary: string,
    secondary: string,
  }),
  id: string,
  onClick: func,
  onEdit: func,
  placeholderPrefix: string,
  error: bool,
  move: bool,
};

export default FillInDetails;
